/**
 * Almacén local sobre IndexedDB.
 *
 * Reemplaza a `localStorage`, que tope en unos 5 MB: el histórico completo son
 * más de 10.000 guías y lo desbordaba. Peor aún, `setItem` lanzaba
 * QuotaExceededError y el error se perdía en un `console.warn`, así que la
 * importación decía «listo» sin haber guardado nada.
 *
 * IndexedDB admite cientos de MB y aquí los errores se propagan.
 */

const NOMBRE_BD = 'xtreme-mining';
const VERSION = 2;

/** Colecciones que existen en la base. Agregar una obliga a subir VERSION. */
export const COLECCIONES = [
  'usuarios',
  'plantas',
  'clientes',
  'productos',
  'centrosCosto',
  'proyectos',
  'destinos',
  'equipos',
  'personas',
  'capitulos',
  'itemsContrato',
  'contratos',
  'guias',
  'estadosPago',
  'talonarios',
  'valoresUf',
  'auditoria',
  'trabajadores',
  'marcasAsistencia',
  'gastosGenerales',
] as const;

let promesaBd: Promise<IDBDatabase> | null = null;

function abrir(): Promise<IDBDatabase> {
  if (promesaBd) return promesaBd;

  promesaBd = new Promise((resolver, rechazar) => {
    const pedido = indexedDB.open(NOMBRE_BD, VERSION);

    pedido.onupgradeneeded = () => {
      const bd = pedido.result;
      for (const c of COLECCIONES) {
        if (!bd.objectStoreNames.contains(c)) bd.createObjectStore(c, { keyPath: 'id' });
      }
    };
    pedido.onsuccess = () => resolver(pedido.result);
    pedido.onerror = () =>
      rechazar(pedido.error ?? new Error('No se pudo abrir la base de datos local.'));
    pedido.onblocked = () =>
      rechazar(
        new Error('Hay otra pestaña con una versión distinta abierta. Ciérrala y reintenta.'),
      );
  });

  return promesaBd;
}

/** Nombre de almacén válido, creando uno genérico si la colección es nueva. */
function almacenValido(bd: IDBDatabase, coleccion: string): string {
  if (bd.objectStoreNames.contains(coleccion)) return coleccion;
  throw new Error(
    `La colección «${coleccion}» no existe en la base local. Súbele la versión al esquema.`,
  );
}

function comoPromesa<T>(pedido: IDBRequest<T>): Promise<T> {
  return new Promise((resolver, rechazar) => {
    pedido.onsuccess = () => resolver(pedido.result);
    pedido.onerror = () => rechazar(pedido.error);
  });
}

export async function leerTodo<T>(coleccion: string): Promise<T[]> {
  const bd = await abrir();
  const nombre = almacenValido(bd, coleccion);
  const tx = bd.transaction(nombre, 'readonly');
  return comoPromesa(tx.objectStore(nombre).getAll() as IDBRequest<T[]>);
}

export async function leerUno<T>(coleccion: string, id: string): Promise<T | undefined> {
  const bd = await abrir();
  const nombre = almacenValido(bd, coleccion);
  const tx = bd.transaction(nombre, 'readonly');
  return comoPromesa(tx.objectStore(nombre).get(id) as IDBRequest<T | undefined>);
}

/** Escribe varios registros en una sola transacción. */
export async function escribir<T extends { id: string }>(
  coleccion: string,
  registros: T[],
): Promise<void> {
  if (registros.length === 0) return;
  const bd = await abrir();
  const nombre = almacenValido(bd, coleccion);

  return new Promise((resolver, rechazar) => {
    const tx = bd.transaction(nombre, 'readwrite');
    const almacen = tx.objectStore(nombre);
    for (const r of registros) almacen.put(r);

    tx.oncomplete = () => resolver();
    tx.onabort = () => {
      const e = tx.error;
      rechazar(
        e?.name === 'QuotaExceededError'
          ? new Error(
              'No queda espacio en el almacenamiento del navegador. ' +
                'Libera espacio o conecta la app a Firebase.',
            )
          : (e ?? new Error('La escritura local fue cancelada.')),
      );
    };
    tx.onerror = () => rechazar(tx.error);
  });
}

export async function borrar(coleccion: string, id: string): Promise<void> {
  const bd = await abrir();
  const nombre = almacenValido(bd, coleccion);
  return new Promise((resolver, rechazar) => {
    const tx = bd.transaction(nombre, 'readwrite');
    tx.objectStore(nombre).delete(id);
    tx.oncomplete = () => resolver();
    tx.onerror = () => rechazar(tx.error);
  });
}

export async function vaciar(coleccion: string): Promise<void> {
  const bd = await abrir();
  const nombre = almacenValido(bd, coleccion);
  return new Promise((resolver, rechazar) => {
    const tx = bd.transaction(nombre, 'readwrite');
    tx.objectStore(nombre).clear();
    tx.oncomplete = () => resolver();
    tx.onerror = () => rechazar(tx.error);
  });
}

export async function contar(coleccion: string): Promise<number> {
  const bd = await abrir();
  const nombre = almacenValido(bd, coleccion);
  const tx = bd.transaction(nombre, 'readonly');
  return comoPromesa(tx.objectStore(nombre).count());
}

/** Espacio usado y disponible, para poder avisar antes de llenar el disco. */
export async function espacio(): Promise<{ usado: number; disponible: number } | null> {
  if (!navigator.storage?.estimate) return null;
  const { usage = 0, quota = 0 } = await navigator.storage.estimate();
  return { usado: usage, disponible: quota };
}

// ── Aviso entre pestañas ──────────────────────────────────────────────────

const CANAL = 'xtreme:cambios';
const canal = 'BroadcastChannel' in globalThis ? new BroadcastChannel(CANAL) : null;
const oyentes = new Map<string, Set<() => void>>();

canal?.addEventListener('message', (e: MessageEvent<string>) => {
  oyentes.get(e.data)?.forEach((fn) => fn());
});

export function avisarCambio(coleccion: string) {
  oyentes.get(coleccion)?.forEach((fn) => fn());
  canal?.postMessage(coleccion);
}

export function escucharCambios(coleccion: string, alCambiar: () => void): () => void {
  if (!oyentes.has(coleccion)) oyentes.set(coleccion, new Set());
  oyentes.get(coleccion)!.add(alCambiar);
  return () => oyentes.get(coleccion)?.delete(alCambiar);
}

/**
 * Migra lo que haya quedado en localStorage de la versión anterior.
 * Se ejecuta una vez y deja el respaldo hasta confirmar que quedó copiado.
 */
export async function migrarDesdeLocalStorage(): Promise<number> {
  const CLAVE_VIEJA = 'xtreme:datos:v1';
  let crudo: string | null = null;
  try {
    crudo = localStorage.getItem(CLAVE_VIEJA);
  } catch {
    return 0;
  }
  if (!crudo) return 0;

  let migrados = 0;
  try {
    const datos = JSON.parse(crudo) as Record<string, Record<string, { id: string }>>;
    for (const [coleccion, registros] of Object.entries(datos)) {
      const filas = Object.values(registros ?? {});
      if (!filas.length) continue;
      try {
        await escribir(coleccion, filas);
        migrados += filas.length;
      } catch {
        // Colección desconocida: se ignora en vez de abortar toda la migración.
      }
    }
    localStorage.removeItem(CLAVE_VIEJA);
  } catch {
    return migrados;
  }
  return migrados;
}
